import React from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarstickLogoMark from "@/components/brand/MarstickLogoMark";
import RouteLoading from "@/components/layout/RouteLoading";
import { useLanguage } from "@/lib/LanguageContext";
import { PublicThemeController, PublicThemeToggle } from "./PublicThemeController";
import { cn } from "@/lib/utils";
import { getShellCopy } from "@/lib/shellTranslations";

const MobileHeader = React.lazy(() => import("@/components/mobile/MobileHeader"));
const MobileBottomNav = React.lazy(() => import("@/components/mobile/MobileBottomNav"));
import PublicSidebar from "./PublicSidebar";
const PublicLanguageSwitcher = React.lazy(() => import("./PublicLanguageSwitcher"));
const PublicAuthButton = React.lazy(() => import("./PublicAuthButton"));
const GlobalHeaderSearch = React.lazy(() => import("./GlobalHeaderSearch"));
import PublicFooter from "./PublicFooter";
const EditorialAttribution = React.lazy(() => import("./EditorialAttribution"));
const PublicInternalLinking = React.lazy(() => import("./PublicInternalLinking"));
const CookieConsentBanner = React.lazy(() => import("@/components/privacy/CookieConsentBanner"));
import AdSenseAutoAds from "@/components/ads/AdSenseAutoAds";
const PublisherAdNetworkLoader = React.lazy(() => import("@/components/ads/PublisherAdNetworkLoader"));
const GoogleAnalyticsRouteTracker = React.lazy(() => import("@/components/analytics/GoogleAnalyticsRouteTracker"));
const NativeSelectMobileSheet = React.lazy(() => import("@/components/mobile/NativeSelectMobileSheet"));

export default function PublicLayout() {
  const [open, setOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [deferScripts, setDeferScripts] = React.useState(false);
  const [isDesktopNav, setIsDesktopNav] = React.useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);
  const [isLargeNav, setIsLargeNav] = React.useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  const params = useParams();
  const { language, applyRouteLanguage } = useLanguage();
  const lang = params.lang || language || "en";
  const shell = getShellCopy(lang);
  const navigate = useNavigate();
  const location = useLocation();
  const isHebrew = lang === "he" || lang === "ar";

  React.useEffect(() => {
    if (params.lang && params.lang !== language) applyRouteLanguage(params.lang);
  }, [params.lang, language, applyRouteLanguage]);

  React.useEffect(() => {
    const show = () => setDeferScripts(true);
    const idleId = window.requestIdleCallback ? window.requestIdleCallback(show, { timeout: 2500 }) : window.setTimeout(show, 1800);
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  React.useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 768px)");
    const largeQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => {
      setIsDesktopNav(desktopQuery.matches);
      setIsLargeNav(largeQuery.matches);
    };
    update();
    desktopQuery.addEventListener("change", update);
    largeQuery.addEventListener("change", update);
    return () => {
      desktopQuery.removeEventListener("change", update);
      largeQuery.removeEventListener("change", update);
    };
  }, []);

  React.useEffect(() => {
    const dir = isHebrew ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lang || "en";
    if (document.body) document.body.dir = dir;
  }, [isHebrew, lang]);

  const openUpgrade = () => navigate("/billing");
  const isMobileMenuRoute = /(^|\/)menu\/?$/.test(location.pathname);
  const preserveScroll = (action) => {
    const x = window.scrollX;
    const y = window.scrollY;
    action();
    requestAnimationFrame(() => window.scrollTo(x, y));
  };

  const headerSearch = deferScripts ? (
    <React.Suspense fallback={null}>
      <GlobalHeaderSearch lang={lang} />
    </React.Suspense>
  ) : (
    <div className="hidden h-11 w-80 md:block" aria-hidden="true" />
  );

  return (
    <div dir={isHebrew ? "rtl" : "ltr"} className="min-h-screen bg-background text-foreground">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-slate-950 focus:px-4 focus:py-3 focus:text-sm focus:font-bold focus:text-white">{shell.skipToMainContent}</a>
      {deferScripts && (
        <React.Suspense fallback={null}>
          <GoogleAnalyticsRouteTracker />
        </React.Suspense>
      )}
      <AdSenseAutoAds />
      {deferScripts && (
        <React.Suspense fallback={null}>
          <PublisherAdNetworkLoader />
        </React.Suspense>
      )}
      <PublicThemeController />
      {!isDesktopNav && (
        <React.Suspense fallback={null}>
          <MobileHeader />
        </React.Suspense>
      )}
      {isDesktopNav && (
      <header className={cn("sticky top-0 z-50 border-b border-slate-200 bg-white/92 text-slate-950 shadow-sm backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/92 dark:text-white", isHebrew ? (collapsed ? "lg:mr-20" : "lg:mr-64") : (collapsed ? "lg:ml-20" : "lg:ml-64"))}>
        <nav className="flex min-h-16 w-full min-w-0 items-center justify-between gap-2 px-3 sm:gap-3 sm:px-6 lg:px-8" aria-label={shell.publicNavigation}>
          <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3">
            <button type="button" className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white/85 text-slate-900 shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-white lg:hidden" onClick={() => preserveScroll(() => setOpen(true))} aria-label={shell.openMenu}><Menu className="h-5 w-5" /></button>
            <Link to={`/${lang || "en"}`} className="flex min-w-0 items-center gap-2 font-heading text-xl font-black leading-none text-slate-950 sm:text-2xl dark:text-white">
              <MarstickLogoMark className="h-10 w-10 rounded-2xl sm:h-11 sm:w-11" />
              <span className="whitespace-nowrap">Marstick</span>
            </Link>
          </div>
          {headerSearch}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Link to={`/${lang}/search`} className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-slate-900 shadow-sm dark:border-white/15 dark:bg-white/10 dark:text-white md:hidden" aria-label={shell.search}>
              <Search className="h-4 w-4" />
            </Link>
            {deferScripts ? (
              <React.Suspense fallback={<div className="h-11 w-20 shrink-0" aria-hidden="true" />}>
                <PublicLanguageSwitcher />
              </React.Suspense>
            ) : <div className="hidden h-11 w-20 shrink-0 sm:block" aria-hidden="true" />}
            <PublicThemeToggle />
            {deferScripts ? (
              <React.Suspense fallback={<div className="hidden h-11 w-24 shrink-0 lg:block" aria-hidden="true" />}>
                <PublicAuthButton />
              </React.Suspense>
            ) : <div className="hidden h-11 w-24 shrink-0 lg:block" aria-hidden="true" />}
            <Button onClick={openUpgrade} className="hidden sm:inline-flex">{shell.upgradeToPremium}</Button>
          </div>
        </nav>
      </header>
      )}

      {isLargeNav && (
      <div
        className="fixed inset-y-0 z-[90]"
        style={isHebrew ? { right: 0, left: "auto", width: collapsed ? "5rem" : "16rem" } : { left: 0, right: "auto", width: collapsed ? "5rem" : "16rem" }}
      >
        <PublicSidebar key={`desktop-${lang}`} lang={lang} collapsed={collapsed} onPremiumClick={openUpgrade} />
        <button
          type="button"
          onClick={() => preserveScroll(() => setCollapsed((value) => !value))}
          className="absolute top-20 grid h-8 w-8 place-items-center rounded-full border border-slate-200 bg-white text-xs font-black text-slate-700 shadow-lg hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/45 dark:border-cyan-100/70 dark:bg-cyan-50 dark:text-slate-950 dark:hover:bg-white"
          style={{ insetInlineEnd: "-0.75rem" }}
          aria-label={shell.collapseSidebar}
        >
          {collapsed ? (isHebrew ? "‹" : "›") : (isHebrew ? "›" : "‹")}
        </button>
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[60] bg-ocean/50 backdrop-blur-sm lg:hidden" onClick={() => preserveScroll(() => setOpen(false))}>
          <div
            className="h-full w-[min(22rem,88vw)] bg-white text-slate-900 shadow-2xl"
            style={isHebrew ? { marginLeft: "auto", marginRight: 0 } : { marginLeft: 0, marginRight: "auto" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 p-4">
              <b className="font-heading text-xl">Marstick</b>
              <button type="button" className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-sm hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/45 dark:border-cyan-100/70 dark:bg-cyan-50 dark:text-slate-950 dark:hover:bg-white" onClick={() => preserveScroll(() => setOpen(false))} aria-label={shell.closeMenu}><X className="h-5 w-5" /></button>
            </div>
            <PublicSidebar key={`mobile-${lang}`} lang={lang} mobile onPremiumClick={openUpgrade} onNavigate={() => preserveScroll(() => setOpen(false))} />
          </div>
        </div>
      )}

      <div className={cn("pb-[calc(8.75rem+env(safe-area-inset-bottom,0px))] md:pb-0", isHebrew ? (collapsed ? "lg:pr-20" : "lg:pr-64") : (collapsed ? "lg:pl-20" : "lg:pl-64"))}>
        <main id="main-content" className="min-w-0 overflow-x-hidden pt-[calc(7.25rem+env(safe-area-inset-top,0px))] md:pt-0">
          <div className="w-full min-w-0 overflow-x-hidden">
            <React.Suspense fallback={<RouteLoading />}>
              <Outlet key={lang || "en"} context={{ openUpgrade }} />
            </React.Suspense>
          </div>
        </main>
        <PublicFooter lang={lang || "en"} />
        {deferScripts && (
          <>
            <React.Suspense fallback={null}>
              <EditorialAttribution lang={lang || "en"} />
            </React.Suspense>
            <React.Suspense fallback={null}>
              <PublicInternalLinking lang={lang || "en"} />
            </React.Suspense>
          </>
        )}
      </div>
      {deferScripts && !isDesktopNav && !isMobileMenuRoute && (
        <React.Suspense fallback={null}>
          <MobileBottomNav />
        </React.Suspense>
      )}
      {deferScripts && !isDesktopNav && (
        <React.Suspense fallback={null}>
          <NativeSelectMobileSheet />
        </React.Suspense>
      )}
      {deferScripts && (
        <React.Suspense fallback={null}>
          <CookieConsentBanner />
        </React.Suspense>
      )}
    </div>
  );
}